import {
  Card,
  BodyText,
  Button,
  EmptyState,
  MetricCard,
  RegulatoryHeatMap,
  Combobox,
} from '@aaimplatform/ui';
import type { ComboboxOption } from '@aaimplatform/ui';
import {
  RefreshCw as RefreshIcon,
  Download as DownloadIcon,
  FileText as FileTextIcon,
  AlertTriangle as WarningIcon,
  ShieldAlert as ShieldAlertIcon,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHeatMap, useDownloadReport, useEntityConfigs } from '../hooks/useRegulatoryReadiness';
import {
  REGULATORY_CORRECTIONS_EMAIL,
  REGULATORY_ENTITY_PARAM as ENTITY_PARAM,
  REGULATORY_PROVENANCE_NOTE,
  entityConfigDescription,
} from '../lib/regulatory-labels';
import { formatCount } from '../utils/display-format';

export function RegulatoryReadinessPage() {
  const navigate = useNavigate();
  // The selected institution profile lives in the URL (?entity=<id>), so the
  // jurisdiction drill-down can return to it and a reload keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEntityId = searchParams.get(ENTITY_PARAM) ?? undefined;

  const { data: heatMapData, isLoading, error, refetch } = useHeatMap(selectedEntityId);
  // The list endpoint returns every entity configuration (no pagination), so
  // it is read once and the Combobox filters label and description
  // client-side. The prior onSearch handler returned the options built from
  // the PREVIOUS query's data, and the Combobox kept showing that stale list.
  const {
    data: entityConfigsData,
    isLoading: entityConfigsLoading,
    error: entityConfigsError,
  } = useEntityConfigs('');
  const downloadReport = useDownloadReport();

  const selectEntity = (entityId: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (entityId) {
      next.set(ENTITY_PARAM, entityId);
    } else {
      next.delete(ENTITY_PARAM);
    }
    setSearchParams(next);
  };

  const handleCellClick = (jurisdictionCode: string) => {
    const query = selectedEntityId
      ? `?${ENTITY_PARAM}=${encodeURIComponent(selectedEntityId)}`
      : '';
    navigate(`/regulatory/${jurisdictionCode}${query}`);
  };

  const handleDownloadExecutiveSummary = () => {
    if (!selectedEntityId) return;
    downloadReport.mutate({
      type: 'executive-summary',
      entityConfigId: selectedEntityId,
    });
  };

  const handleDownloadFullAnalysis = () => {
    if (!selectedEntityId) return;
    downloadReport.mutate({
      type: 'readiness-analysis',
      entityConfigId: selectedEntityId,
    });
  };

  const entityOptions: ComboboxOption[] = (entityConfigsData?.configs ?? []).map(config => ({
    id: config.id,
    label: config.name,
    description: entityConfigDescription(config),
  }));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <BodyText variant="ui" className="text-tertiary">
            Regulatory readiness for pledged-asset lending, by jurisdiction and asset class
          </BodyText>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {selectedEntityId && (
            <>
              <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={20} />
                Refresh
              </Button>
              <Button
                variant="secondary"
                onClick={handleDownloadExecutiveSummary}
                disabled={downloadReport.isPending}
              >
                <DownloadIcon className="mr-2" size={20} />
                Executive Summary
              </Button>
              <Button
                variant="primary"
                onClick={handleDownloadFullAnalysis}
                disabled={downloadReport.isPending}
              >
                <FileTextIcon className="mr-2" size={20} />
                Full Analysis
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Institution profile selector */}
      <Card className="p-4 mb-6">
        <Combobox
          options={entityOptions}
          value={selectedEntityId}
          onChange={(val) => selectEntity(typeof val === 'string' && val !== '' ? val : undefined)}
          placeholder="Search institution profiles..."
          label="Institution profile"
          description="Select an institution profile to view its regulatory readiness assessment"
          isLoading={entityConfigsLoading}
          errorMessage={
            entityConfigsError
              ? `Institution profiles could not be loaded: ${entityConfigsError.message}`
              : undefined
          }
          isClearable
        />
      </Card>

      {/* Empty state when no institution profile is selected */}
      {!selectedEntityId && (
        <EmptyState
          icon={<ShieldAlertIcon size={48} />}
          title="Select an institution profile"
          description="Choose an institution profile from the selector above to view its regulatory readiness heat map, signal assessments, and downloadable reports."
        />
      )}

      {/* Error state */}
      {selectedEntityId && error && (
        <Card className="p-6 text-center">
          <WarningIcon className="text-error mb-2 mx-auto" size={32} />
          <BodyText variant="ui" className="text-error mb-4">
            Failed to load regulatory readiness data
          </BodyText>
          <BodyText variant="ui" className="text-tertiary text-sm mb-4">
            {error.message}
          </BodyText>
          <Button variant="primary" onClick={() => refetch()}>
            <RefreshIcon className="mr-2" size={20} />
            Retry
          </Button>
        </Card>
      )}

      {/* Loading state */}
      {selectedEntityId && isLoading && !heatMapData && (
        <Card className="p-6 text-center">
          <BodyText variant="ui" className="text-tertiary">
            Loading heat map...
          </BodyText>
        </Card>
      )}

      {/* Heat map and summary when data loaded */}
      {selectedEntityId && heatMapData && !error && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <MetricCard label="Total Cells" value={formatCount(heatMapData.summary.totalCells)} />
            <MetricCard label="Green" value={formatCount(heatMapData.summary.greenCount)} />
            <MetricCard label="Yellow" value={formatCount(heatMapData.summary.yellowCount)} />
            <MetricCard label="Red" value={formatCount(heatMapData.summary.redCount)} />
          </div>

          {/* Heat Map */}
          <Card className="p-4">
            <RegulatoryHeatMap
              entityConfigName={heatMapData.entityConfigName}
              generatedAt={heatMapData.generatedAt}
              assetClassGroups={heatMapData.assetClassGroups}
              rows={heatMapData.rows}
              summary={heatMapData.summary}
              onCellClick={handleCellClick}
            />
          </Card>
        </>
      )}

      {/* Data provenance footer, shared with the jurisdiction profile page. */}
      <div className="mt-8 pt-4 border-t border-recessed">
        <BodyText variant="ui" className="text-xs text-tertiary">
          {REGULATORY_PROVENANCE_NOTE}{' '}
          <a
            href={`mailto:${REGULATORY_CORRECTIONS_EMAIL}`}
            className="text-secondary underline underline-offset-2 hover:text-primary"
          >
            {REGULATORY_CORRECTIONS_EMAIL}
          </a>
          .
        </BodyText>
      </div>
    </div>
  );
}
